import type { ComponentPropsWithoutRef, RefObject } from 'react';
import { createContext, forwardRef, useMemo, useRef, useState } from 'react';
import type { Tooltip } from '@frameio/vapor';
import { useFeatureGateLabelActiveContext } from '../hooks/useFeatureGateLabelActiveContext';
import type { DisableProps } from './Disable';
import { Disable } from './Disable';
import type { FeatureGateLinkWithTooltipProps } from './FeatureGateModalButtonWithTooltip';
import { FeatureGateModalButtonWithTooltip } from './FeatureGateModalButtonWithTooltip';

type FeatureGateProps = DisableProps & {
  /** The placement prop of the Tooltip */
  placement?: ComponentPropsWithoutRef<typeof Tooltip>['placement'];
  /** Whether to render a Tooltip */
  shouldShowTooltip?: boolean;
  /** Optional styling for the upgrade modal button that wraps the feature */
  wrapperStyles?: FeatureGateLinkWithTooltipProps['wrapperStyles'];
} & Pick<FeatureGateLinkWithTooltipProps, 'onModalButtonClick'>;

export type FeatureGateDisableContextValue =
  | {
      isUpgradeModalButtonActive: boolean;
      /**
       * If we nest a FeatureGateLabel inside of a FeatureGate component, the tooltip
       * that wraps that component will be disabled. We will instead show the tooltip
       * that wraps the FeatureGate component. To make sure the tooltip is positioned
       * over the FeatureGateLabel, we set the anchorRef in context.
       */
      tooltipAnchorRef: RefObject<HTMLElement | undefined | null>;
    }
  | undefined;

export const FeatureGateDisableContext =
  createContext<FeatureGateDisableContextValue>(undefined);

/**
 * Wraps a component to disable all of the children.
 * By default there is no visual indication that the children are disabled.
 * Pass in `disabledStyles` to style the disabled children.
 */
export const FeatureGate = forwardRef(
  (
    {
      as,
      children,
      disabledStyles,
      feature,
      onModalButtonClick,
      placement,
      shouldOverrideFeatureGate,
      shouldShowTooltip = false,
      wrapperStyles,
    }: FeatureGateProps,
    forwardedRef
  ) => {
    const featureGateLabelActiveContext = useFeatureGateLabelActiveContext();
    const [isUpgradeModalButtonActive, setIsUpgradeModalButtonActive] =
      useState(false);
    const tooltipAnchorRef = useRef(undefined);
    const contextValue = useMemo(
      () => ({
        isUpgradeModalButtonActive,
        tooltipAnchorRef,
      }),
      [isUpgradeModalButtonActive]
    );
    return (
      <FeatureGateDisableContext.Provider value={contextValue}>
        <FeatureGateModalButtonWithTooltip
          feature={feature}
          onModalButtonClick={onModalButtonClick}
          placement={placement}
          setIsUpgradeModalButtonActive={
            featureGateLabelActiveContext?.[1] ?? setIsUpgradeModalButtonActive
          }
          shouldOverrideFeatureGate={shouldOverrideFeatureGate}
          tooltipAnchorRef={shouldShowTooltip ? undefined : tooltipAnchorRef}
          wrapperStyles={wrapperStyles}
        >
          <Disable
            ref={forwardedRef}
            as={as}
            disabledStyles={disabledStyles}
            feature={feature}
            shouldOverrideFeatureGate={shouldOverrideFeatureGate}
          >
            {children}
          </Disable>
        </FeatureGateModalButtonWithTooltip>
      </FeatureGateDisableContext.Provider>
    );
  }
);
